import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
} from '@angular/material/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { MerchantService } from '../../../services/merchant.service';
import { Constants } from '../../../app.constants';
import { AutomationDialogComponent } from '../automation-dialog/automation-dialog.component';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';

@Component({
  selector: 'app-automation-details-dialog',
  templateUrl: './automation-details-dialog.component.html',
  styleUrls: ['./automation-details-dialog.component.css'],
})
export class AutomationDetailsDialogComponent implements OnInit {
  automation: any;
  executions: any[] = [];
  loading = false;
  selectedTab: number = 0;
  dateFilterControl = new FormControl('all');
  dateFilterOptions: { value: string; label: string }[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AutomationDetailsDialogComponent>,
    private merchantService: MerchantService,
    public constants: Constants,
    private dialog: MatDialog,
  ) {
    this.automation = data.automation;
  }

  ngOnInit(): void {
    this.generateDateFilterOptions();
    this.loadExecutions();
  }

  generateDateFilterOptions(): void {
    const options = [{ value: 'all', label: 'All Time' }];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed

    // Generate options for the last 24 months
    for (let i = 0; i < 24; i++) {
      const date = new Date(currentYear, currentMonth - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // Convert to 1-indexed
      const monthStr = month.toString().padStart(2, '0');
      const value = `${year}-${monthStr}`;
      const label = date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
      });
      options.push({ value, label });
    }

    this.dateFilterOptions = options;
  }

  loadExecutions(): void {
    this.loading = true;
    const dateFilter = this.dateFilterControl.value || 'all';
    this.merchantService
      .getAutomationExecutions(this.automation._id, dateFilter)
      .subscribe({
        next: (res: any) => {
          this.executions = res;
        },
        error: (err: any) => {
          console.error('Error loading executions:', err);
          this.constants.snack('Failed to load execution history');
        },
      })
      .add(() => (this.loading = false));
  }

  onDateFilterChange(): void {
    this.loadExecutions();
  }

  editAutomation(): void {
    if (this.isScheduleAutomation()) {
      this.constants.snack(
        'Scheduled automations are temporarily unavailable in the editor',
      );
      return;
    }

    const dialogRef = this.dialog.open(AutomationDialogComponent, {
      data: { automation: this.automation },
      scrollStrategy: new NoopScrollStrategy(),
      width: '800px',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dialogRef.close(true);
      }
    });
  }

  deleteAutomation(): void {
    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Delete Automation',
        body: `Are you sure you want to delete the automation "${this.automation.name}"? This action cannot be undone.`,
        buttonOne: 'Cancel',
        buttonTwo: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe((option: number) => {
      if (option === 1) {
        this.merchantService.deleteAutomation(this.automation._id).subscribe({
          next: () => {
            this.constants.snack('Automation deleted successfully');
            this.dialogRef.close(true);
          },
          error: (err: any) => {
            console.error('Error deleting automation:', err);
            this.constants.snack('Failed to delete automation');
          },
        });
      }
    });
  }

  toggleEnabled(): void {
    const newEnabledState = !this.automation.enabled;
    this.merchantService
      .updateAutomation(this.automation._id, { enabled: newEnabledState })
      .subscribe({
        next: () => {
          this.automation.enabled = newEnabledState;
          this.constants.snack(
            `Automation ${newEnabledState ? 'enabled' : 'disabled'} successfully`,
          );
        },
        error: (err: any) => {
          console.error('Error updating automation:', err);
          this.constants.snack('Failed to update automation');
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  getTargetDisplay(): string {
    if (this.automation.target?.type === 'all_customers') {
      return 'All Customers';
    } else if (this.automation.target?.type === 'filtered_customers') {
      return 'Filtered Customers';
    }
    return '-';
  }

  getTriggerDisplay(): string {
    if (this.automation.trigger?.type === 'customer_takes_loyalty_card') {
      return 'Customer takes loyalty card';
    }
    return 'Schedule';
  }

  getFrequencyDisplay(): string {
    const freq = this.automation.frequency;
    if (!freq?.type) {
      return '-';
    }

    switch (freq.type) {
      case 'once':
        return `Once on ${new Date(freq.date).toLocaleDateString()}`;
      case 'daily':
        return `Daily at ${freq.time}`;
      case 'weekly':
        return `Weekly on ${this.getDayName(freq.dayOfWeek)} at ${freq.time}`;
      case 'monthly':
        return `Monthly on day ${freq.dayOfMonth} at ${freq.time}`;
      case 'specific_date':
        return `On ${new Date(freq.date).toLocaleDateString()} at ${freq.time}`;
      case 'milestone':
        return `When customer reaches ${freq.milestoneThreshold} stamps`;
      default:
        return '-';
    }
  }

  getDayName(day: number): string {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return days[day] || '-';
  }

  getActiveFilters(): string[] {
    if (!this.automation.target?.filters) {
      return [];
    }

    const filters = this.automation.target?.filters;
    const activeFilters: string[] = [];

    // Map filter keys to user-friendly labels
    const filterLabels: { [key: string]: string } = {
      birthdayThisMonth: 'Birthday this month',
      receivedReward: 'Has received a reward',
      redeemedReward: 'Has redeemed a reward',
      receivedStamp: 'Has never received a stamp or points',
      receivedNotification: 'Has received a notification',
      hasVoucher: 'Has at least one voucher',
      unredeemedRewards: 'Has unredeemed rewards',
      lapsedStamps: 'Has not received a stamp or points',
    };

    // Check each filter and add to active list if enabled
    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      // Skip if filter is false or undefined
      if (!value) {
        return;
      }

      // Handle lapsedStampsDays separately as it's a number
      if (key === 'lapsedStampsDays') {
        return; // This is handled with lapsedStamps
      }

      // Add the filter with its label
      if (filterLabels[key]) {
        let label = filterLabels[key];

        // Add additional info for lapsed stamps
        if (key === 'lapsedStamps' && filters.lapsedStampsDays) {
          label += ` (${filters.lapsedStampsDays} days)`;
        }

        activeFilters.push(label);
      }
    });

    // Add card filters
    if (
      this.automation.target?.cards &&
      this.automation.target?.cards?.length > 0
    ) {
      const cardCount = this.automation.target?.cards?.length || 0;
      activeFilters.push(
        `Has ${cardCount} specific card${cardCount > 1 ? 's' : ''}`,
      );
    }

    // Add voucher filters
    if (
      this.automation.target?.vouchers &&
      this.automation.target?.vouchers?.length > 0
    ) {
      const voucherCount = this.automation.target?.vouchers?.length || 0;
      activeFilters.push(
        `Has ${voucherCount} specific voucher${voucherCount > 1 ? 's' : ''}`,
      );
    }

    // Add voucher status filters
    if (
      this.automation.target?.voucherStatus &&
      this.automation.target?.voucherStatus?.length > 0
    ) {
      const statuses = this.automation.target?.voucherStatus?.join(', ');
      activeFilters.push(`Voucher status: ${statuses}`);
    }

    return activeFilters;
  }

  getConditionDisplay(): string[] {
    const cards = this.automation.conditions?.cards || [];
    if (!cards.length) {
      return ['All loyalty cards'];
    }

    return cards.map((card: any) => card?.title || 'Loyalty card');
  }

  hasActiveFilters(): boolean {
    return this.getActiveFilters().length > 0;
  }

  isScheduleAutomation(): boolean {
    return !this.automation.trigger?.type || this.automation.trigger.type === 'schedule';
  }
}
